<template>
  <!--
    Atlas compass-rose mark. Inline SVG rather than a raster so it stays
    crisp at every avatar size and inherits `currentColor` where possible.

    Two palette modes:
      mono  — single-tone (currentColor), good for tiny indicators.
      brand — the canonical two-tone look: deep indigo for the primary
              axis + centre, lighter purple for the secondary rays.
  -->
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 200 200"
    :width="size"
    :height="size"
    role="img"
    :aria-label="ariaLabel"
    class="atlas-logo"
  >
    <title>Ask Atlas</title>

    <circle cx="100" cy="100" r="85" fill="none" :stroke="darkColor" stroke-width="3" />
    <circle cx="100" cy="100" r="62" fill="none" :stroke="darkColor" stroke-width="1" />

    <g :stroke="darkColor" stroke-width="1.5" stroke-linecap="round">
      <line x1="100" y1="22" x2="100" y2="28" />
      <line x1="100" y1="172" x2="100" y2="178" />
      <line x1="22" y1="100" x2="28" y2="100" />
      <line x1="172" y1="100" x2="178" y2="100" />
      <line x1="44.8" y1="44.8" x2="48.7" y2="48.7" />
      <line x1="151.3" y1="44.8" x2="155.2" y2="48.7" />
      <line x1="44.8" y1="155.2" x2="48.7" y2="151.3" />
      <line x1="151.3" y1="155.2" x2="155.2" y2="151.3" />
    </g>

    <!-- Cardinal rays: north in deep indigo, south/east/west in purple accent -->
    <polygon points="100,30 114,100 86,100" :fill="darkColor" />
    <polygon points="100,170 114,100 86,100" :fill="lightColor" />
    <polygon points="30,100 100,86 100,114" :fill="lightColor" />
    <polygon points="170,100 100,86 100,114" :fill="lightColor" />

    <!-- Inter-cardinal rays, set back in depth via opacity -->
    <polygon points="52,52 100,96 96,100" :fill="darkColor" opacity="0.65" />
    <polygon points="148,52 100,96 104,100" :fill="darkColor" opacity="0.65" />
    <polygon points="52,148 100,104 96,100" :fill="darkColor" opacity="0.65" />
    <polygon points="148,148 100,104 104,100" :fill="darkColor" opacity="0.65" />

    <!-- Pivot: solid indigo with a clean white inset dot -->
    <circle cx="100" cy="100" r="5" :fill="darkColor" />
    <circle cx="100" cy="100" r="2" fill="#ffffff" />
  </svg>
</template>

<script>
import { computed, defineComponent } from 'vue';

// Brand tokens — match the app's existing indigo→purple gradient so Atlas
// feels native to the rest of the portal chrome rather than an imported
// brand.
const ATLAS_DARK = '#4F46E5';  // indigo-600 — primary, ring, N cardinal
const ATLAS_LIGHT = '#A78BFA'; // purple-400 — S/E/W cardinals

export default defineComponent({
  name: 'AtlasLogo',
  props: {
    size: { type: [Number, String], default: 40 },
    variant: {
      type: String,
      default: 'brand',
      validator: (v) => v === 'brand' || v === 'mono',
    },
    ariaLabel: { type: String, default: 'Atlas' },
  },
  setup(props) {
    const darkColor = computed(() =>
      props.variant === 'mono' ? 'currentColor' : ATLAS_DARK,
    );
    const lightColor = computed(() =>
      props.variant === 'mono' ? 'currentColor' : ATLAS_LIGHT,
    );
    return { darkColor, lightColor };
  },
});
</script>

<style scoped>
.atlas-logo {
  display: inline-block;
  flex-shrink: 0;
}
</style>
